import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import TicketInfoBlock from '../TicketInfoBlock';
import ErrorBoundary from '../ErrorBoundary';

const REQUEST_TIMEOUT_MS = 15_000;

export default function App({ blockId, blockData }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(() => {
    const apiUrl = blockData?.apiUrl;
    if (!apiUrl) {
      setError('Missing API URL — unable to load ticket information.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    axios
      .get(apiUrl, {
        withCredentials: true,
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      })
      .then((res) => {
        if (!res.data || typeof res.data !== 'object') {
          throw new Error('Invalid response from server.');
        }
        setData(res.data);
      })
      .catch((err) => {
        if (axios.isCancel?.(err) || err.name === 'CanceledError') {
          setError('The request timed out. Please try again.');
          return;
        }
        const status = err.response?.status;
        let msg;
        if (status === 404) {
          msg = 'Event not found.';
        } else if (status === 403 || status === 401) {
          msg = 'You are not authorized to view this event.';
        } else if (status >= 500) {
          msg = 'Server error — please try again later.';
        } else if (!navigator.onLine) {
          msg = 'You appear to be offline. Please check your connection.';
        } else {
          msg = err.response?.data?.message || err.message || `Request failed (${status || 'unknown'}).`;
        }
        setError(msg);
      })
      .finally(() => {
        clearTimeout(timeoutId);
        setLoading(false);
      });

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [blockData?.apiUrl]);

  useEffect(() => {
    const cleanup = fetchData();
    return cleanup;
  }, [fetchData]);

  if (loading) {
    return (
      <div className="ticket-block-loader" aria-hidden="true">
        <span className="ticket-block-loader__spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="ticket-app__error" role="alert">
        <p>{error}</p>
        <button
          type="button"
          className="ticket-app__retry"
          onClick={fetchData}
        >
          Retry
        </button>
      </div>
    );
  }

  // Prefer user from PHP template (blockData.user); API often doesn't receive cookies.
  const mergedData = { ...data };
  if (blockData?.user != null && (blockData.user.logged_in || blockData.user.uid > 0)) {
    mergedData.user = blockData.user;
  }

  if (!mergedData.show_ticket_box) {
    return (
      <p className="ticket-app__expired">
        Registration for this event has closed.
      </p>
    );
  }

  return (
    <ErrorBoundary>
      <TicketInfoBlock data={mergedData} blockId={blockId} />
    </ErrorBoundary>
  );
}
